import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  StudioToolbar,
  BlockLibraryRail,
  CanvasViewport,
  PropertyInspector,
  LayerTreePanel,
  RevisionDiffDrawer,
  SectionPickerModal,
  PageManagerDrawer,
  type SectionPreset,
  useCanvasHistory,
  usePageDraftSync,
  findNodeById,
  type ViewportMode,
  type BlockNode,
  type DocumentAST,
  PreviewTopbar,
  TemplateBundlePickerModal,
  SiteNavigationContext,
  SITE_BUNDLES,
  type WebsiteBundle,
  type BundlePageTemplate,
  applyPageTemplateToSiteChrome,
} from '..';
import { CodeExportModal } from '../components/CodeExportModal';
import { PromotionsManagerDrawer } from '../components/PromotionsManagerDrawer';
import type { StyleTargetState } from '../types';
import { SiteExportImportModal, governanceApi } from '../../governance';
import { editorApi, type SiteRecord, type PageRecord } from '../api';
import { useBreakpoint } from '@shared/hooks/use-breakpoint';
import { useAuth } from '@shared/auth/AuthContext';
import { Globe, Plus, Sparkles, Check, Layers, LayoutGrid } from 'lucide-react';
import { PAGE_PRESETS } from '../templates/pagePresets';

const webbuilderApi = { ...editorApi, ...governanceApi };

const initialRoot: BlockNode = {
  id: 'root-1',
  type: 'Container',
  category: 'layout',
  props: {},
  styles: { base: { padding: '0px' } },
  children: [
    {
      id: 'hero-1',
      type: 'HeroSplit',
      category: 'layout',
      props: {
        headline: 'Welcome to your Premier Coaching Academy',
        subheadline: 'Empowering students to achieve top ranks in JEE, NEET, and Foundation examinations.',
        ctaText: 'Explore Courses',
        ctaLink: '#courses',
      },
      styles: { base: { padding: '56px 24px', backgroundColor: '#f8fafc' } },
    },
    {
      id: 'course-cat-1',
      type: 'CoachCourseCatalogBlock',
      category: 'erp_binding',
      props: {
        title: 'Upcoming Entrance Batches',
        text: 'Upcoming Entrance Batches',
      },
      styles: { base: { padding: '56px 24px', backgroundColor: '#ffffff' } },
    },
  ],
};

const defaultDoc: DocumentAST = {
  version: '1.0',
  page_id: 'default-page',
  tenant_id: 'default-tenant',
  root: initialRoot,
  seo: {
    title: 'Home - Premier Coaching Academy',
    description: 'Top coaching institute for JEE & NEET entrance preparation.',
  },
};

export function PagesStudioPage(): React.JSX.Element {
  const { isDesktop } = useBreakpoint();
  const { hasPermission } = useAuth();
  const canPublish = hasPermission('webbuilder.publish') || hasPermission('*');

  const [viewport, setViewport] = useState<ViewportMode>('desktop');
  const [selectedId, setSelectedId] = useState<string | null>('hero-1');
  const [leftTab, setLeftTab] = useState<'blocks' | 'layers'>('blocks');
  const [isMobileInspectorOpen, setIsMobileInspectorOpen] = useState(false);
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const [isZenMode, setIsZenMode] = useState(false);

  // GrapesJS Canvas & Right Dock Studio States
  const [showOutlines, setShowOutlines] = useState(true);
  const [showSpacingGuides, setShowSpacingGuides] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [activeStyleState, setActiveStyleState] = useState<StyleTargetState>('default');
  const [highlightedBoxPart, setHighlightedBoxPart] = useState<'margin' | 'padding' | null>(null);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [rightDockTab, setRightDockTab] = useState<'style' | 'traits' | 'layers'>('style');

  // Site and Page State
  const [sites, setSites] = useState<SiteRecord[]>([]);
  const [activeSiteId, setActiveSiteId] = useState<string>('');
  const [pages, setPages] = useState<PageRecord[]>([]);
  const [activePageId, setActivePageId] = useState<string>('');
  const [isLoadingSites, setIsLoadingSites] = useState(true);

  // Modals and Drawers
  const [showCreateSiteModal, setShowCreateSiteModal] = useState(false);
  const [showNewPageModal, setShowNewPageModal] = useState(false);
  const [showDiffDrawer, setShowDiffDrawer] = useState(false);
  const [showExportImportModal, setShowExportImportModal] = useState(false);
  const [showSectionPickerModal, setShowSectionPickerModal] = useState(false);
  const [sectionPickerIndex, setSectionPickerIndex] = useState<number | undefined>(undefined);
  const [showPageManagerDrawer, setShowPageManagerDrawer] = useState(false);
  const [showPromotionsDrawer, setShowPromotionsDrawer] = useState(false);
  const [showTemplateBundleModal, setShowTemplateBundleModal] = useState(false);
  const [diffVersion, setDiffVersion] = useState<number>(1);
  const [diffList, setDiffList] = useState<any[]>([]);

  // Form State
  const [newSiteName, setNewSiteName] = useState('');
  const [newSiteSlug, setNewSiteSlug] = useState('');
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPagePath, setNewPagePath] = useState('');
  const [selectedPresetId, setSelectedPresetId] = useState<string>('blank');
  const [publishSuccessMsg, setPublishSuccessMsg] = useState<string | null>(null);
  const [publishApprovalStatus, setPublishApprovalStatus] = useState<'draft' | 'pending_approval' | 'published'>('draft');

  const history = useCanvasHistory(defaultDoc);
  const sync = usePageDraftSync({ pageId: activePageId, doc: history.doc });

  // Stable ref to resetDoc to prevent infinite re-render loops (AUD-06)
  const resetDocRef = useRef(history.resetDoc);
  resetDocRef.current = history.resetDoc;

  const handleClearCanvas = useCallback(() => {
    if (window.confirm('Are you sure you want to clear all blocks from this page?')) {
      history.resetDoc({
        ...history.doc,
        root: {
          id: 'root-1',
          type: 'Container',
          category: 'layout',
          props: {},
          styles: { base: { padding: '0px' } },
          children: [],
        },
      });
      setSelectedId(null);
    }
  }, [history]);

  // Active Site and Page records
  const activeSite = sites.find((s) => s.id === activeSiteId);
  const activePage = pages.find((p) => p.id === activePageId);

  // Load sites on mount
  const loadSites = useCallback(async () => {
    setIsLoadingSites(true);
    try {
      const data = await webbuilderApi.listSites();
      if (Array.isArray(data) && data.length > 0) {
        setSites(data);
        setActiveSiteId(data[0].id);
      } else {
        setSites([]);
        setActiveSiteId('');
      }
    } catch {
      setSites([]);
    } finally {
      setIsLoadingSites(false);
    }
  }, []);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  // Load pages when activeSiteId changes
  useEffect(() => {
    if (!activeSiteId) {
      setPages([]);
      setActivePageId('');
      return;
    }
    let isMounted = true;
    (async () => {
      try {
        const pageList = await webbuilderApi.listPages(activeSiteId);
        if (isMounted && Array.isArray(pageList) && pageList.length > 0) {
          setPages(pageList);
          const firstPage = pageList[0];
          setActivePageId(firstPage.id);
          if (firstPage.draft_ast_json && firstPage.draft_ast_json.root) {
            resetDocRef.current({
              ...firstPage.draft_ast_json,
              page_id: firstPage.id,
              tenant_id: firstPage.tenant_id || firstPage.draft_ast_json.tenant_id,
            });
          } else {
            resetDocRef.current({
              ...defaultDoc,
              page_id: firstPage.id,
              tenant_id: firstPage.tenant_id,
            });
          }
        } else if (isMounted) {
          setPages([]);
          setActivePageId('');
        }
      } catch {
        // Fallback
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [activeSiteId]);

  // Handle switching page
  const handleSelectPage = async (pageId: string) => {
    if (pageId === activePageId) return;
    await sync.saveNow();
    setActivePageId(pageId);
    try {
      const pageData = await webbuilderApi.getPage(pageId);
      if (pageData && pageData.draft_ast_json && pageData.draft_ast_json.root) {
        resetDocRef.current({
          ...pageData.draft_ast_json,
          page_id: pageId,
          tenant_id: pageData.tenant_id || pageData.draft_ast_json.tenant_id,
        });
        setSelectedId(pageData.draft_ast_json.root.id);
      }
    } catch {
      // Keep existing doc
    }
  };

  // Create new site handler
  const handleCreateSite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSiteName || !newSiteSlug) return;

    try {
      const site = await webbuilderApi.createSite({
        name: newSiteName,
        subdomain_slug: newSiteSlug.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
      });
      // Automatically create the root home page
      const initialHome = await webbuilderApi.createPage(site.id, {
        title: 'Home',
        route_path: '/',
        page_type: 'custom',
      });
      setSites((prev) => [...prev, site]);
      setActiveSiteId(site.id);
      setPages([initialHome]);
      setActivePageId(initialHome.id);
      resetDocRef.current({
        ...defaultDoc,
        page_id: initialHome.id,
      });
      setShowCreateSiteModal(false);
      setNewSiteName('');
      setNewSiteSlug('');
    } catch {
      setShowCreateSiteModal(false);
    }
  };

  // Handle creating new page
  const handleCreatePage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeSiteId || !newPageTitle || !newPagePath) return;

    try {
      const preset = PAGE_PRESETS.find((p) => p.id === selectedPresetId) || PAGE_PRESETS[0];
      const newPage = await webbuilderApi.createPage(activeSiteId, {
        title: newPageTitle,
        route_path: newPagePath.startsWith('/') ? newPagePath : `/${newPagePath}`,
        page_type: 'custom',
      });
      const rootNode = preset.generateRoot(newPage.id);
      const newDoc: DocumentAST = {
        version: '1.0',
        page_id: newPage.id,
        tenant_id: newPage.tenant_id || activeSite?.tenant_id || '',
        root: rootNode,
        seo: {
          title: newPage.title,
          description: '',
        },
      };
      await webbuilderApi.saveDraft(newPage.id, { draft_ast: newDoc });
      setPages((prev) => [...prev, newPage]);
      setActivePageId(newPage.id);
      resetDocRef.current(newDoc);
      setSelectedId(rootNode.id);
      setShowNewPageModal(false);
      setNewPageTitle('');
      setNewPagePath('');
      setSelectedPresetId('blank');
    } catch {
      setShowNewPageModal(false);
    }
  };

  // Apply complete 5-page website bundle (Requirement 10)
  const handleApplyFullBundle = async (bundle: WebsiteBundle) => {
    if (!activeSiteId) return;
    try {
      // 1. Apply bundle's landing page to current active page
      const landingTpl = bundle.pages.find((p) => p.archetype === 'landing') || bundle.pages[0];
      const newRoot = landingTpl.generateRoot(activePageId, activeSite?.name || bundle.fancyName);
      if (newRoot.children && newRoot.children.length > 0) {
        const navNode = newRoot.children.find((c) => c.type === 'SiteNavbar');
        if (navNode) {
          navNode.props = { ...navNode.props, variant: bundle.navbarVariant, accentColor: bundle.palette.primary };
        }
        const footNode = newRoot.children.find((c) => c.type === 'SiteFooter');
        if (footNode) {
          footNode.props = { ...footNode.props, variant: bundle.footerVariant, accentColor: bundle.palette.secondary };
        }
      }
      history.resetDoc({
        ...history.doc,
        root: newRoot,
      });

      // 2. Ensure all other core bundle pages exist
      const existingPaths = new Set(pages.map((p) => p.route_path));
      for (const tpl of bundle.pages) {
        if (tpl.archetype !== 'landing' && !existingPaths.has(tpl.defaultPath)) {
          const created = await webbuilderApi.createPage(activeSiteId, {
            title: tpl.title,
            route_path: tpl.defaultPath,
            page_type: 'custom',
          });
          const pageRoot = tpl.generateRoot(created.id, activeSite?.name || bundle.fancyName);
          await webbuilderApi.saveDraft(created.id, {
            draft_ast: {
              version: '1.0',
              page_id: created.id,
              tenant_id: created.tenant_id,
              root: pageRoot,
              seo: { title: `${tpl.title} - ${bundle.fancyName}`, description: tpl.description },
            },
            seo: { title: `${tpl.title} - ${bundle.fancyName}`, description: tpl.description },
          });
        }
      }

      const updatedPages = await webbuilderApi.listPages(activeSiteId);
      setPages(updatedPages);
      setPublishSuccessMsg(`✨ Applied "${bundle.fancyName}" bundle! 5 pages synchronized with live navbar.`);
      setTimeout(() => setPublishSuccessMsg(null), 4000);
    } catch {
      // Fallback
    }
  };

  // Add new page with chosen template
  const handleAddNewPageWithTemplate = async (template: BundlePageTemplate) => {
    if (!activeSiteId) return;
    try {
      const uniquePath = pages.some((p) => p.route_path === template.defaultPath)
        ? `${template.defaultPath}-${Date.now().toString(36).slice(-3)}`
        : template.defaultPath;

      const newPage = await webbuilderApi.createPage(activeSiteId, {
        title: template.title,
        route_path: uniquePath,
        page_type: 'custom',
      });

      const newRoot = template.generateRoot(newPage.id, activeSite?.name || template.bundleName);
      await webbuilderApi.saveDraft(newPage.id, {
        draft_ast: {
          version: '1.0',
          page_id: newPage.id,
          tenant_id: newPage.tenant_id,
          root: newRoot,
          seo: { title: `${template.title} - ${template.bundleName}`, description: template.description },
        },
        seo: { title: `${template.title} - ${template.bundleName}`, description: template.description },
      });

      const updatedPages = await webbuilderApi.listPages(activeSiteId);
      setPages(updatedPages);
      await handleSelectPage(newPage.id);
      setPublishSuccessMsg(`Added page "${template.title}" from ${template.bundleName}!`);
      setTimeout(() => setPublishSuccessMsg(null), 3000);
    } catch {
      // Error handling
    }
  };

  // Quick 1-click turnkey page creator (from PageManagerDrawer)
  const handleQuickCreatePage = async (title: string, route: string, presetId: string) => {
    if (!activeSiteId) return;
    try {
      const preset = PAGE_PRESETS.find((p) => p.id === presetId) || PAGE_PRESETS[0];
      const newPage = await webbuilderApi.createPage(activeSiteId, {
        title,
        route_path: route.startsWith('/') ? route : `/${route}`,
        page_type: 'custom',
      });
      const rootNode = preset.generateRoot(newPage.id);
      const newDoc: DocumentAST = {
        version: '1.0',
        page_id: newPage.id,
        tenant_id: newPage.tenant_id || activeSite?.tenant_id || '',
        root: rootNode,
        seo: {
          title: newPage.title,
          description: '',
        },
      };
      await webbuilderApi.saveDraft(newPage.id, { draft_ast: newDoc });
      setPages((prev) => [...prev, newPage]);
      setActivePageId(newPage.id);
      resetDocRef.current(newDoc);
      setSelectedId(rootNode.id);
      setShowPageManagerDrawer(false);
    } catch (err) {
      console.error('Failed to create turnkey page:', err);
    }
  };

  // Insert ready-made section preset onto canvas
  const handleInsertSectionPreset = (sec: SectionPreset, insertIndex?: number) => {
    const clonedBlocks = JSON.parse(JSON.stringify(sec.blocks)).map((b: BlockNode) => ({
      ...b,
      id: `${b.type.toLowerCase()}-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`,
    }));

    clonedBlocks.forEach((block: BlockNode, idx: number) => {
      const targetIdx = insertIndex !== undefined ? insertIndex + idx : undefined;
      history.insertNodeAt(history.doc.root.id, block, targetIdx);
    });

    if (clonedBlocks.length > 0) {
      setSelectedId(clonedBlocks[0].id);
    }
  };

  // Keyboard shortcuts (Ctrl+Z, Ctrl+Y, Ctrl+S)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        if (!isInput) {
          e.preventDefault();
          if (e.shiftKey) {
            history.redo();
          } else {
            history.undo();
          }
        }
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        if (!isInput) {
          e.preventDefault();
          history.redo();
        }
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
        if (!isInput && selectedId && selectedId !== history.doc.root.id) {
          e.preventDefault();
          const newId = history.duplicateNode(selectedId);
          if (newId) setSelectedId(newId);
        }
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (!isInput && selectedId && selectedId !== history.doc.root.id) {
          e.preventDefault();
          history.removeNode(selectedId);
          setSelectedId(null);
        }
      } else if (e.key === 'Escape') {
        setSelectedId(null);
        if (isZenMode) setIsZenMode(false);
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        sync.saveNow();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [history, sync, selectedId, isZenMode]);

  // Node selection with recursive traversal
  const selectedNode = selectedId ? findNodeById(history.doc.root, selectedId) : null;

  const handleSelectNode = (id: string) => {
    setSelectedId(id || null);
    if (id && !isDesktop) {
      setIsMobileInspectorOpen(true);
    }
  };

  const handlePublish = async () => {
    if (!activePageId) return;
    if (!canPublish) {
      setPublishApprovalStatus('pending_approval');
      return;
    }
    const res = await sync.publishPage();
    if (res) {
      setPublishSuccessMsg(`Published version ${res.version_no} live!`);
      setPublishApprovalStatus('published');
      setTimeout(() => setPublishSuccessMsg(null), 4000);
    }
  };

  const handleOpenDiff = async () => {
    if (!activePageId) return;
    try {
      const diffData = await webbuilderApi.getRevisionDiff(activePageId, diffVersion);
      const mappedDiffs = (diffData?.changes || []).map((c: any) => ({
        node_id: c.path || 'node',
        type: 'Block',
        action: (c.op === 'add' ? 'added' : c.op === 'remove' ? 'removed' : 'modified') as 'added' | 'removed' | 'modified',
        changed_fields: [c.path],
      }));
      setDiffList(mappedDiffs);
      setShowDiffDrawer(true);
    } catch {
      setShowDiffDrawer(true);
    }
  };

  const handleExportSite = async () => {
    if (!activeSiteId) return;
    try {
      const bundle = await webbuilderApi.exportSiteBundle(activeSiteId);
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `site-bundle-${activeSiteId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Handle error
    }
  };

  const handleImportSite = async (jsonStr: string) => {
    if (!activeSiteId) return;
    const bundle = JSON.parse(jsonStr);
    await webbuilderApi.importSiteBundle(activeSiteId, bundle);
    await loadSites();
  };

  // Honest empty state when no site exists (AUD-05)
  if (!isLoadingSites && sites.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-4rem)] p-6 bg-background text-center">
        <div className="max-w-md w-full p-8 rounded-2xl bg-card border border-border shadow-lg flex flex-col items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
            <Globe className="h-7 w-7" />
          </div>
          <h2 className="text-xl font-bold text-foreground">No Website Created Yet</h2>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Launch a storefront for your coaching academy. Showcase JEE/NEET courses, announce results, display toppers, and collect student admission enquiries.
          </p>
          <button
            type="button"
            onClick={() => setShowCreateSiteModal(true)}
            className="w-full h-11 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-bold transition flex items-center justify-center gap-2 cursor-pointer shadow hover:opacity-90 min-h-[44px]"
          >
            <Plus className="h-4 w-4" />
            <span>Create Institute Website</span>
          </button>
        </div>

        {/* Create Site Modal */}
        {showCreateSiteModal && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-xl max-w-md w-full p-6 shadow-2xl flex flex-col gap-4 text-left">
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">Create Institute Website</h3>
              </div>
              <form onSubmit={handleCreateSite} className="flex flex-col gap-4">
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">Website Name</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Apex IIT Academy"
                    value={newSiteName}
                    onChange={(e) => {
                      setNewSiteName(e.target.value);
                      if (!newSiteSlug) {
                        setNewSiteSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '-'));
                      }
                    }}
                    className="w-full text-sm p-2 rounded-md border border-border bg-background text-foreground"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">Subdomain Slug</label>
                  <div className="flex items-center rounded-md border border-border bg-background overflow-hidden">
                    <input
                      type="text"
                      required
                      placeholder="e.g. apex-academy"
                      value={newSiteSlug}
                      onChange={(e) => setNewSiteSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                      className="w-full text-sm p-2 bg-transparent text-foreground focus:outline-none"
                    />
                    <span className="text-xs text-muted-foreground px-2.5 bg-muted py-2 border-l border-border">
                      .sites.thesci.co
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                  <button
                    type="button"
                    onClick={() => setShowCreateSiteModal(false)}
                    className="px-4 py-2 rounded-md border border-border hover:bg-muted text-sm font-medium transition-colors min-h-[44px]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-bold hover:opacity-90 transition-opacity min-h-[44px]"
                  >
                    Create & Launch
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={
        isZenMode
          ? 'fixed inset-0 z-50 bg-background flex flex-col overflow-hidden select-none animate-in fade-in duration-150'
          : 'flex flex-col h-[calc(100vh-4rem)] md:h-screen overflow-hidden bg-background'
      }
    >
      {/* UNIFIED STUDIO TOOLBAR (Animated withdrawal in preview mode) */}
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden shrink-0 ${
          mode === 'preview'
            ? 'max-h-0 opacity-0 -translate-y-full pointer-events-none'
            : 'max-h-14 opacity-100 translate-y-0'
        }`}
      >
        <StudioToolbar
          viewport={viewport}
          onViewportChange={setViewport}
          canUndo={history.canUndo}
          canRedo={history.canRedo}
          onUndo={history.undo}
          onRedo={history.redo}
          syncStatus={sync.status}
          onPublish={handlePublish}
          mode={mode}
          onModeChange={setMode}
          sites={sites}
          activeSiteId={activeSiteId}
          onSelectSite={setActiveSiteId}
          onCreateSite={() => setShowCreateSiteModal(true)}
          pages={pages}
          activePageId={activePageId}
          onSelectPage={handleSelectPage}
          onCreatePage={() => setShowNewPageModal(true)}
          onManagePages={() => setShowPageManagerDrawer(true)}
          onOpenTemplatesBundles={() => setShowTemplateBundleModal(true)}
          onOpenPromotions={() => setShowPromotionsDrawer(true)}
          onOpenHistory={handleOpenDiff}
          onOpenExportImport={() => setShowExportImportModal(true)}
          isZenMode={isZenMode}
          onToggleZenMode={() => setIsZenMode((v) => !v)}
          publishApprovalStatus={publishApprovalStatus}
          showOutlines={showOutlines}
          onToggleOutlines={() => setShowOutlines((v) => !v)}
          showSpacingGuides={showSpacingGuides}
          onToggleSpacingGuides={() => setShowSpacingGuides((v) => !v)}
          zoom={zoom}
          onZoomChange={setZoom}
          onOpenCodeModal={() => setShowCodeModal(true)}
          onClearCanvas={handleClearCanvas}
          activeRightDockTab={rightDockTab}
          onRightDockTabChange={setRightDockTab}
        />
      </div>

      {/* SINGLE SLIM PREVIEW TOPBAR (Requirement 7: slides in on preview, 5m auto-save status, Publish, Enable Editor) */}
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden shrink-0 ${
          mode === 'preview'
            ? 'max-h-10 opacity-100 translate-y-0'
            : 'max-h-0 opacity-0 -translate-y-full pointer-events-none'
        }`}
      >
        <PreviewTopbar
          viewport={viewport}
          onViewportChange={setViewport}
          pages={pages}
          activePageId={activePageId}
          onSelectPage={handleSelectPage}
          isDirty={sync.isDirty}
          isSaving={sync.status === 'saving'}
          lastSavedAt={sync.lastSavedAt}
          onSaveDraft={() => sync.saveNow()}
          onPublish={handlePublish}
          onEnableEditor={() => setMode('edit')}
        />
      </div>

      {/* Floating Notification Toast */}
      {publishSuccessMsg && (
        <div className="fixed bottom-12 right-6 z-50 flex items-center gap-2 bg-emerald-600 text-white px-4 py-2.5 rounded-xl shadow-2xl text-xs font-bold animate-in fade-in slide-in-from-bottom-3 duration-200">
          <Check className="h-4 w-4" />
          <span>{publishSuccessMsg}</span>
        </div>
      )}

      {/* Workspace Body */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Left Side: Unified Component Rail (Smooth 300ms withdrawal slide animation) */}
        <div
          className={`hidden md:flex flex-col border-r border-border bg-card transition-all duration-300 ease-in-out shrink-0 overflow-hidden ${
            mode === 'preview'
              ? 'w-0 -translate-x-full opacity-0 border-r-0 pointer-events-none'
              : 'w-60 translate-x-0 opacity-100'
          }`}
        >
          <BlockLibraryRail
            onInsertBlock={(type, category) => {
              const targetParent = selectedId || history.doc.root.id;
              const newNode: BlockNode = {
                id: `${type.toLowerCase()}-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`,
                type,
                category,
                props: {
                  text: `${type} Block`,
                  headline: 'Empowering Academic Excellence',
                  subheadline: 'Join top rankers in our entrance preparation program.',
                  label: type,
                  title: type,
                },
                styles: { base: {} },
              };
              history.insertNode(targetParent, newNode);
              setSelectedId(newNode.id);
            }}
            onInsertSection={(sec) => handleInsertSectionPreset(sec)}
          />
        </div>

        {/* Center: Canvas Viewport with Live Student Navigation Context */}
        <SiteNavigationContext.Provider
          value={{
            pages,
            activePageId,
            siteName: activeSite?.name || 'Apex Coaching Academy',
            isPreview: mode === 'preview',
            onNavigatePage: (pageId) => handleSelectPage(pageId),
          }}
        >
          <CanvasViewport
            viewport={viewport}
            rootNode={history.doc.root}
            selectedNodeId={selectedId}
            onSelectNode={handleSelectNode}
            mode={mode}
            siteSlug={activeSite?.subdomain_slug || 'academy'}
            pagePath={activePage?.route_path || '/'}
            activeState={activeStyleState}
            showOutlines={showOutlines}
            showSpacingGuides={showSpacingGuides}
            highlightedBoxPart={highlightedBoxPart}
            zoom={zoom}
            onInsertNodeAt={history.insertNodeAt}
            onMoveNode={history.moveNode}
            onMoveNodeTo={history.moveNodeTo}
            onDuplicateNode={(id) => {
              const newId = history.duplicateNode(id);
              if (newId) setSelectedId(newId);
            }}
            onWrapInContainer={(id) => {
              const containerId = history.wrapInContainer(id);
              if (containerId) setSelectedId(containerId);
            }}
            onDeleteNode={(id) => {
              history.removeNode(id);
              setSelectedId(null);
            }}
            onUpdateProps={history.updateNodeProps}
            onResizeNode={(nodeId, patch) => {
              history.updateNodeStylesTarget(nodeId, patch, {
                breakpoint: viewport,
                state: activeStyleState,
              });
            }}
            onOpenSectionPicker={(idx) => {
              setSectionPickerIndex(idx);
              setShowSectionPickerModal(true);
            }}
          />
        </SiteNavigationContext.Provider>

        {/* Right Side: Property Inspector (Smooth 300ms withdrawal slide animation) */}
        <div
          className={`hidden md:flex flex-col border-l border-border bg-card transition-all duration-300 ease-in-out shrink-0 overflow-hidden ${
            mode === 'preview'
              ? 'w-0 translate-x-full opacity-0 border-l-0 pointer-events-none'
              : 'w-64 translate-x-0 opacity-100'
          }`}
        >
          <PropertyInspector
            selectedNode={selectedNode}
            rootNode={history.doc.root}
            activeTab={rightDockTab}
            onTabChange={setRightDockTab}
            activeBreakpoint={viewport}
            activeState={activeStyleState}
            onStateChange={setActiveStyleState}
            onUpdateProps={history.updateNodeProps}
            onUpdateStylesTarget={history.updateNodeStylesTarget}
            onUpdateMeta={history.updateNodeMeta}
            onDeleteNode={(id) => {
              history.removeNode(id);
              setSelectedId(null);
            }}
            onSelectNode={handleSelectNode}
            onToggleHide={(id, hidden) => {
              history.updateNodeMeta(id, { hidden });
            }}
            onToggleLock={(id, locked) => {
              history.updateNodeMeta(id, { locked });
            }}
            onHoverBoxPart={setHighlightedBoxPart}
            isOpenMobile={isMobileInspectorOpen}
            onCloseMobile={() => setIsMobileInspectorOpen(false)}
            pages={pages}
          />
        </div>
      </div>

      {/* Turnkey Section Picker Modal */}
      <SectionPickerModal
        isOpen={showSectionPickerModal}
        onClose={() => setShowSectionPickerModal(false)}
        insertIndex={sectionPickerIndex}
        onSelectSection={(sec) => {
          handleInsertSectionPreset(sec, sectionPickerIndex);
          setShowSectionPickerModal(false);
        }}
      />

      {/* Page & Navigation Manager Drawer */}
      <PageManagerDrawer
        isOpen={showPageManagerDrawer}
        onClose={() => setShowPageManagerDrawer(false)}
        pages={pages}
        activePageId={activePageId}
        onSelectPage={handleSelectPage}
        onNewPage={() => {
          setShowPageManagerDrawer(false);
          setShowNewPageModal(true);
        }}
        onDeletePage={async (id) => {
          try {
            await webbuilderApi.deletePage(id);
            const remaining = pages.filter((p) => p.id !== id);
            setPages(remaining);
            if (activePageId === id && remaining.length > 0) {
              handleSelectPage(remaining[0].id);
            }
          } catch (err) {
            console.error('Failed to delete page:', err);
          }
        }}
        onRenamePage={async (id, title, routePath) => {
          try {
            await webbuilderApi.updatePage(id, { title, route_path: routePath });
            setPages((prev) =>
              prev.map((p) => (p.id === id ? { ...p, title, route_path: routePath } : p))
            );
          } catch (err) {
            console.error('Failed to rename page:', err);
          }
        }}
        onQuickCreatePage={handleQuickCreatePage}
      />

      {/* Promotional Notice Banners & Popup Modals Drawer */}
      <PromotionsManagerDrawer
        isOpen={showPromotionsDrawer}
        onClose={() => setShowPromotionsDrawer(false)}
        siteId={activeSiteId}
        initialSettings={activeSite?.settings_json as any}
      />

      {/* Revision Diff Drawer */}
      <RevisionDiffDrawer
        isOpen={showDiffDrawer}
        versionNo={diffVersion}
        diffs={diffList}
        onClose={() => setShowDiffDrawer(false)}
        onRestore={async (ver) => {
          if (activePageId) {
            await webbuilderApi.rollbackPage(activePageId, ver);
            setShowDiffDrawer(false);
            const pageData = await webbuilderApi.getPage(activePageId);
            if (pageData?.draft_ast_json) {
              resetDocRef.current(pageData.draft_ast_json);
            }
          }
        }}
      />

      {/* Export / Import Modal */}
      <SiteExportImportModal
        isOpen={showExportImportModal}
        siteId={activeSiteId}
        onClose={() => setShowExportImportModal(false)}
        onExport={handleExportSite}
        onImport={handleImportSite}
      />

      {/* New Page Modal */}
      {showNewPageModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl max-w-lg w-full p-6 shadow-2xl flex flex-col gap-4 text-left max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <div>
                <h2 className="text-lg font-semibold text-foreground">Create New Page</h2>
                <p className="text-xs text-muted-foreground">Select a pre-built template preset or start with a blank page</p>
              </div>
            </div>

            {/* Template Presets Picker */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground block">Choose Template</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {PAGE_PRESETS.map((preset) => {
                  const isSelected = selectedPresetId === preset.id;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => {
                        setSelectedPresetId(preset.id);
                        setNewPageTitle(preset.defaultTitle);
                        setNewPagePath(preset.defaultPath);
                      }}
                      className={`p-2.5 rounded-lg border text-left flex flex-col justify-between gap-1 transition-all text-xs min-h-[68px] ${
                        isSelected
                          ? 'border-primary ring-2 ring-primary/20 bg-primary/5 text-foreground'
                          : 'border-border bg-background hover:bg-muted text-muted-foreground'
                      }`}
                    >
                      <div className="font-bold text-foreground text-xs leading-tight">{preset.name}</div>
                      <div className="text-[10px] text-muted-foreground line-clamp-1">{preset.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <form onSubmit={handleCreatePage} className="flex flex-col gap-3 pt-2 border-t border-border">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Page Title</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. JEE Advanced Entrance Program"
                  value={newPageTitle}
                  onChange={(e) => setNewPageTitle(e.target.value)}
                  className="w-full text-xs p-2 rounded-md border border-border bg-background text-foreground focus:ring-1 focus:ring-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Route Path</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. /toppers or /terms-and-conditions"
                  value={newPagePath}
                  onChange={(e) => setNewPagePath(e.target.value)}
                  className="w-full text-xs p-2 rounded-md border border-border bg-background text-foreground focus:ring-1 focus:ring-primary focus:outline-none font-mono"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => setShowNewPageModal(false)}
                  className="px-4 py-2 rounded-md border border-border hover:bg-muted text-xs font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 transition-opacity"
                >
                  Create &amp; Open Page
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Site Modal */}
      {showCreateSiteModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl max-w-md w-full p-6 shadow-2xl flex flex-col gap-4 text-left">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold text-foreground">Create Institute Website</h3>
            </div>
            <form onSubmit={handleCreateSite} className="flex flex-col gap-4">
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1">Website Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Apex IIT Academy"
                  value={newSiteName}
                  onChange={(e) => {
                    setNewSiteName(e.target.value);
                    if (!newSiteSlug) {
                      setNewSiteSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '-'));
                    }
                  }}
                  className="w-full text-sm p-2 rounded-md border border-border bg-background text-foreground"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1">Subdomain Slug</label>
                <div className="flex items-center rounded-md border border-border bg-background overflow-hidden">
                  <input
                    type="text"
                    required
                    placeholder="e.g. apex-academy"
                    value={newSiteSlug}
                    onChange={(e) => setNewSiteSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                    className="w-full text-sm p-2 bg-transparent text-foreground focus:outline-none"
                  />
                  <span className="text-xs text-muted-foreground px-2.5 bg-muted py-2 border-l border-border">
                    .sites.thesci.co
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => setShowCreateSiteModal(false)}
                  className="px-4 py-2 rounded-md border border-border hover:bg-muted text-sm font-medium transition-colors min-h-[44px]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-bold hover:opacity-90 transition-opacity min-h-[44px]"
                >
                  Create & Launch
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 10 Bundles & 50+ Interchangeable Page Templates Modal */}
      <TemplateBundlePickerModal
        isOpen={showTemplateBundleModal}
        onClose={() => setShowTemplateBundleModal(false)}
        currentRoot={history.doc.root}
        onApplyTemplateToCurrentPage={(newRoot) => {
          history.resetDoc({
            ...history.doc,
            root: newRoot,
          });
        }}
        onApplyFullBundle={handleApplyFullBundle}
        onAddNewPageWithTemplate={handleAddNewPageWithTemplate}
      />

      {/* GrapesJS Code Viewer & Exporter Modal (<>) */}
      <CodeExportModal
        isOpen={showCodeModal}
        onClose={() => setShowCodeModal(false)}
        doc={history.doc}
      />
    </div>
  );
}

export const handle = { crumb: 'Pages & Visual Studio' };
